import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Link,
  TextField,
  Alert,
} from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import axios from "axios";
import { useSearchParams, useNavigate } from "react-router-dom";

const DL = {
  bgDark: "#0f1419",
  bgMedium: "#1a1f26",
  bgLight: "#242930",
  green: "#62f481",
  greenEmerald: "#44aa5a",
  textPrimary: "#f9f9cf",
  textSecondary: "#99a1af",
  textMuted: "#6a7282",
  border: "rgba(255,255,255,0.08)",
  borderSubtle: "rgba(255,255,255,0.15)",
  borderGreen: "rgba(98,244,129,0.3)",
  divider: "rgba(98,244,129,0.2)",
  red: "#ff6467",
};

function VerifyEmailPage() {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resendIdentifier, setResendIdentifier] = useState(
    searchParams.get("pendingUsername") || searchParams.get("email") || ""
  );
  const [resending, setResending] = useState(false);
  const [resendSuccessMessage, setResendSuccessMessage] = useState("");
  const [resendErrorMessage, setResendErrorMessage] = useState("");
  const navigate = useNavigate();

  const hasVerified = useRef(false);

  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (hasVerified.current) return;
    hasVerified.current = true;

    const token = searchParams.get("token");
    if (!token) {
      setLoading(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await fetch(
          `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/verify?token=${encodeURIComponent(token)}`
        );
        const data = await response.json();

        if (response.ok) {
          setSuccess(true);
        } else {
          setErrorMessage(
            data.message || "Verification failed. Please try again."
          );
        }
      } catch (error) {
        setErrorMessage(
          "An error occurred during verification. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  /* Derived display state */
  const isError = !loading && !!errorMessage;
  const isSuccess = !loading && success;
  const showManualVerification = !loading && !success;

  const handleResendVerification = async () => {
    if (!resendIdentifier.trim()) {
      setResendErrorMessage("Enter your username or email so we can send you a fresh verification link.");
      setResendSuccessMessage("");
      return;
    }

    setResending(true);
    setResendErrorMessage("");
    setResendSuccessMessage("");

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_SC_API_BASE_URL}/api/auth/resend-verification`,
        { identifier: resendIdentifier.trim() }
      );
      setResendSuccessMessage(
        response.data?.message || "If an unverified account matches that username or email, we've sent a new verification email."
      );
    } catch (error) {
      setResendErrorMessage(
        error.response?.data?.message || "We couldn't resend the verification email right now. Please try again."
      );
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: DL.bgDark,
        background: `radial-gradient(ellipse at 50% 0%, rgba(98,244,129,0.06) 0%, ${DL.bgDark} 60%)`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        px: 2,
      }}
    >
      {/* Back link */}
      <Box sx={{ width: "100%", maxWidth: 480, mb: 3 }}>
        <Link
          component="button"
          onClick={() => navigate("/signin")}
          underline="none"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 0.5,
            color: DL.textSecondary,
            fontSize: "0.875rem",
            cursor: "pointer",
            "&:hover": { color: DL.textPrimary },
          }}
        >
          <ArrowBackIcon sx={{ fontSize: 16 }} />
          Back to Sign In
        </Link>
      </Box>

      {/* Card */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 480,
          backgroundColor: DL.bgMedium,
          border: `1px solid ${DL.border}`,
          borderRadius: "12px",
          p: { xs: 3, sm: 4 },
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
        }}
      >
        {/* Loading state */}
        {loading && (
          <>
            <CircularProgress
              size={48}
              thickness={3}
              sx={{ color: DL.green, mb: 3 }}
            />
            <Typography
              component="h1"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: DL.textPrimary,
                mb: 1,
              }}
            >
              Email Verification
            </Typography>
            <Typography sx={{ fontSize: "0.9375rem", color: DL.textSecondary }}>
              Verifying your email address…
            </Typography>
          </>
        )}

        {/* Success state */}
        {isSuccess && (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: "rgba(98,244,129,0.08)",
                border: `1px solid ${DL.borderGreen}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              <MarkEmailReadIcon sx={{ fontSize: 36, color: DL.green }} />
            </Box>

            <Typography
              component="h1"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: DL.textPrimary,
                mb: 1.5,
              }}
            >
              Email Verified!
            </Typography>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                backgroundColor: "rgba(98,244,129,0.08)",
                border: `1px solid ${DL.borderGreen}`,
                borderRadius: "8px",
                px: 2,
                py: 1.25,
                mb: 3,
              }}
            >
              <CheckCircleOutlineIcon
                sx={{ fontSize: 18, color: DL.green, flexShrink: 0 }}
              />
              <Typography
                sx={{ fontSize: "0.875rem", color: DL.green, lineHeight: 1.5 }}
              >
                Email verified successfully! You can now sign in.
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/signin?newUser=true")}
              sx={{
                background: "linear-gradient(to right, #10b981, #059669)",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                py: 1.25,
                fontSize: "0.9375rem",
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(to right, #059669, #047857)",
                  boxShadow: "none",
                },
              }}
            >
              Sign In & Complete Profile
            </Button>
          </>
        )}

        {/* Manual / error state */}
        {showManualVerification && (
          <>
            <Box
              sx={{
                width: 72,
                height: 72,
                borderRadius: "50%",
                backgroundColor: isError
                  ? "rgba(255,100,103,0.08)"
                  : "rgba(98,244,129,0.08)",
                border: isError
                  ? "1px solid rgba(255,100,103,0.3)"
                  : `1px solid ${DL.borderGreen}`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mb: 3,
              }}
            >
              {isError ? (
                <ErrorOutlineIcon sx={{ fontSize: 36, color: DL.red }} />
              ) : (
                <MailOutlineIcon sx={{ fontSize: 36, color: DL.green }} />
              )}
            </Box>

            <Typography
              component="h1"
              sx={{
                fontSize: "1.5rem",
                fontWeight: 700,
                color: DL.textPrimary,
                mb: 1.5,
              }}
            >
              {isError ? "Verification Failed" : "Verify Your Email"}
            </Typography>

            {isError ? (
              <Box
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  backgroundColor: "rgba(255,100,103,0.08)",
                  border: "1px solid rgba(255,100,103,0.3)",
                  borderRadius: "8px",
                  px: 2,
                  py: 1.25,
                  mb: 2,
                  textAlign: "left",
                }}
              >
                <ErrorOutlineIcon
                  sx={{ fontSize: 18, color: DL.red, flexShrink: 0, mt: "2px" }}
                />
                <Typography
                  sx={{ fontSize: "0.875rem", color: DL.red, lineHeight: 1.5 }}
                >
                  {errorMessage}
                </Typography>
              </Box>
            ) : (
              <Typography
                sx={{
                  fontSize: "0.9375rem",
                  color: DL.textSecondary,
                  lineHeight: 1.6,
                  mb: 2,
                }}
              >
                Your account exists, but you still need to verify your email
                before signing in.
              </Typography>
            )}

            <Typography
              sx={{
                fontSize: "0.875rem",
                color: DL.textMuted,
                lineHeight: 1.6,
                mb: 3,
              }}
            >
              Check your spam, junk, or promotions folder first. If your last
              link expired after 24 hours, we can send you a fresh one.
            </Typography>

            <TextField
              fullWidth
              label="Username or Email"
              value={resendIdentifier}
              onChange={(event) => setResendIdentifier(event.target.value)}
              sx={{
                mb: 2,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: DL.bgDark,
                  borderRadius: "8px",
                  "& fieldset": { borderColor: DL.borderSubtle },
                  "&:hover fieldset": { borderColor: DL.borderGreen },
                  "&.Mui-focused fieldset": { borderColor: DL.green, borderWidth: "1.5px" },
                },
                "& .MuiInputBase-input": {
                  color: DL.textPrimary,
                  fontSize: "0.9rem",
                },
                "& .MuiInputLabel-root": { color: DL.textSecondary, fontSize: "0.9rem" },
                "& .MuiInputLabel-root.Mui-focused": { color: DL.green },
              }}
            />

            {resendSuccessMessage && (
              <Alert
                severity="success"
                sx={{
                  width: "100%",
                  mb: 2,
                  borderRadius: "8px",
                  backgroundColor: "rgba(98,244,129,0.1)",
                  color: DL.green,
                  "& .MuiAlert-icon": { color: DL.green },
                }}
              >
                {resendSuccessMessage}
              </Alert>
            )}

            {resendErrorMessage && (
              <Alert
                severity="error"
                sx={{
                  width: "100%",
                  mb: 2,
                  borderRadius: "8px",
                  backgroundColor: "rgba(255,100,103,0.1)",
                  color: DL.red,
                  border: "1px solid rgba(255,100,103,0.25)",
                  "& .MuiAlert-icon": { color: DL.red },
                }}
              >
                {resendErrorMessage}
              </Alert>
            )}

            <Button
              fullWidth
              variant="outlined"
              onClick={handleResendVerification}
              disabled={resending}
              sx={{
                borderColor: DL.borderGreen,
                color: DL.green,
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                py: 1.25,
                fontSize: "0.9375rem",
                mb: 2,
                "&:hover": {
                  borderColor: DL.green,
                  backgroundColor: "rgba(98,244,129,0.08)",
                },
                "&.Mui-disabled": {
                  borderColor: DL.borderSubtle,
                  color: DL.textMuted,
                },
              }}
            >
              {resending ? "Sending New Link..." : "Resend Verification Email"}
            </Button>

            <Button
              fullWidth
              variant="contained"
              onClick={() => navigate("/signin")}
              sx={{
                background: "linear-gradient(to right, #10b981, #059669)",
                color: "#fff",
                borderRadius: "8px",
                textTransform: "none",
                fontWeight: 700,
                py: 1.25,
                fontSize: "0.9375rem",
                boxShadow: "none",
                "&:hover": {
                  background: "linear-gradient(to right, #059669, #047857)",
                  boxShadow: "none",
                },
              }}
            >
              Go to Sign In
            </Button>
          </>
        )}
      </Box>
    </Box>
  );
}

export default VerifyEmailPage;
